import styled, { css } from 'styled-components';
import { layout, zIndex } from 'tokens';

type BarMode = 'focus' | 'prototype';

// The solid bar — visual chrome only. Click target is HitArea below.
export const Bar = styled.div<{ mode: BarMode; left: number; width: number }>`
	position: relative;
	display: flex;
	align-items: center;
	height: ${layout.frameBarHeight}px;
	background: ${({ theme }) => theme.gray[0]};
	border-top: 1px solid ${({ theme }) => theme.gray[200]};

	${({ mode, left, width }) =>
		mode === 'prototype'
			? css`
					grid-area: footer;
					width: 100%;
			  `
			: css`
					position: absolute;
					bottom: 0;
					left: ${left}px;
					width: ${width}px;
					z-index: ${zIndex.island};
			  `}
`;

// Inner content: arrows | hit area | dimensions, in flex flow
export const BarContent = styled.div`
	position: relative;
	display: flex;
	align-items: stretch;
	width: 100%;
	height: 100%;
`;

// The clickable / hoverable region between arrows and dimensions. This is
// the real play/close hit target. Arrows and dimensions sit outside it,
// with a 6px gap on each side so the highlight never runs up against them.
export const HitArea = styled.div`
	flex: 1;
	min-width: 0;
	margin: 0 6px;
	display: flex;
	align-items: center;
	transition: background 80ms ease;

	&:hover {
		background: ${({ theme }) => theme.gray[100]};
	}
	&:active {
		background: ${({ theme }) => theme.gray[200]};
	}
`;

// Center group: Play icon — pinned to the horizontal center of the Bar,
// which already spans the open canvas (left/width driven by
// openCanvasLeft/openCanvasWidth), so 50% of the bar == canvas-center.
// Pointer-events disabled so clicks fall through to HitArea. Focus mode only.
export const CenterGroup = styled.div`
	position: absolute;
	left: 50%;
	top: 0;
	bottom: 0;
	transform: translateX(-50%);
	display: flex;
	align-items: center;
	gap: 0;
	z-index: ${zIndex.island};
	pointer-events: none;
`;

// Prototype-mode exit hint. Centered within HitArea (which is itself
// centered in the prototype canvas column), low-contrast, click-through.
export const GhostLabel = styled.div`
	flex: 1;
	min-width: 0;
	text-align: center;
	font-size: 13px;
	font-weight: 400;
	color: ${({ theme }) => theme.gray[400]};
	letter-spacing: 0.02em;
	pointer-events: none;
	user-select: none;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

// Left group: arrow navigation — sits outside the hit area
export const LeftGroup = styled.div`
	display: flex;
	align-items: center;
	gap: 0;
	flex-shrink: 0;
	margin-left: 4px;
`;

// Right group: DS controls + frame dimensions readout. Sits outside the
// hit area. Wrapper itself is pointer-events: none so dimensions stay
// non-clickable; DsControls re-enables pointer events on its own subtree.
export const RightGroup = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	flex-shrink: 0;
	pointer-events: none;
	margin-right: 12px;
`;

export const Dimensions = styled.div`
	margin-top: 1px;
	font-size: 11px;
	font-weight: 400;
	color: ${({ theme }) => theme.gray[600]};
	font-variant-numeric: tabular-nums;
`;

// Play/Close icon wrapper. No click handler of its own — HitArea behind it
// is the click target. Clicks on the icon pass through via pointer-events.
export const ActionButton = styled.div<{ active: boolean }>`
	display: flex;
	align-items: center;
	justify-content: center;
	height: 32px;
	padding: 0 12px;
	background: transparent;
	opacity: ${({ active }) => (active ? 1 : 0.4)};
`;

// Arrow button within arrow group. Hover/active are bumped one step darker
// than the HitArea's hover/active so arrows still pop against the lit bar.
export const ArrowButton = styled.div<{ disabled: boolean }>`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	border-radius: 4px;
	background: transparent;
	opacity: ${({ disabled }) => (disabled ? 0.4 : 1)};

	&:hover {
		background: ${({ theme, disabled }) => (disabled ? 'transparent' : theme.gray[200])};
	}
	&:active {
		background: ${({ theme, disabled }) => (disabled ? 'transparent' : theme.gray[400])};
	}
`;

export const ArrowSpacer = styled.div`
	width: 1px;
	height: 22px;
	background: ${({ theme }) => theme.gray[200]};
`;
